/**
 * Mermaid rendering for `div.mermaid-diagram[data-src]` blocks in lesson HTML.
 *
 * Mermaid is a heavy dependency, so it is imported on first use and only when a lesson actually
 * contains a diagram. `startOnLoad` is off: we render each block ourselves so a failing diagram
 * leaves its source on the page instead of blanking the lesson.
 */

type MermaidModule = typeof import("mermaid")["default"];

let modulePromise: Promise<MermaidModule> | null = null;
let initialisedFor: "light" | "dark" | null = null;
let counter = 0;

async function getMermaid(scheme: "light" | "dark"): Promise<MermaidModule> {
  if (!modulePromise) {
    modulePromise = import("mermaid").then((mod) => mod.default);
  }
  const mermaid = await modulePromise;
  if (initialisedFor !== scheme) {
    const dark = scheme === "dark";
    mermaid.initialize({
      startOnLoad: false,
      securityLevel: "strict",
      theme: "base",
      fontFamily:
        'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      themeVariables: dark
        ? {
          background: "#151c22",
          primaryColor: "#1e1f35",
          primaryBorderColor: "#a79ff2",
          primaryTextColor: "#e5eae9",
          secondaryColor: "#122319",
          secondaryBorderColor: "#70c49c",
          tertiaryColor: "#1b242a",
          tertiaryBorderColor: "#3d4c55",
          lineColor: "#8597a0",
          textColor: "#e5eae9",
          mainBkg: "#1b242a",
          nodeBorder: "#a79ff2",
          clusterBkg: "#11171c",
          clusterBorder: "#3d4c55",
          edgeLabelBackground: "#151c22",
          fontSize: "14px",
        }
        : {
          background: "#ffffff",
          primaryColor: "#ecebf7",
          primaryBorderColor: "#4a41a8",
          primaryTextColor: "#16222b",
          secondaryColor: "#e4efe9",
          secondaryBorderColor: "#2c6a52",
          tertiaryColor: "#f1f3f2",
          tertiaryBorderColor: "#a7b4b3",
          lineColor: "#6c7d86",
          textColor: "#16222b",
          mainBkg: "#ffffff",
          nodeBorder: "#4a41a8",
          clusterBkg: "#f7f8f7",
          clusterBorder: "#ccd6d4",
          edgeLabelBackground: "#ffffff",
          fontSize: "14px",
        },
    });
    initialisedFor = scheme;
  }
  return mermaid;
}

/**
 * Renders every diagram inside `container`. Safe to call again after a theme change: the source
 * is kept in `data-src`, so a diagram can be redrawn any number of times.
 */
export async function renderDiagrams(
  container: HTMLElement,
  scheme: "light" | "dark",
): Promise<void> {
  const blocks = Array.from(
    container.querySelectorAll<HTMLElement>("div.mermaid-diagram[data-src]"),
  );
  if (blocks.length === 0) return;

  for (const block of blocks) {
    block.dataset.state = "loading";
    block.textContent = "";
  }

  let mermaid: MermaidModule;
  try {
    mermaid = await getMermaid(scheme);
  } catch {
    for (const block of blocks) showSource(block, "Diagrams could not be loaded.");
    return;
  }

  for (const block of blocks) {
    const source = block.dataset.src ?? "";
    if (!source.trim()) {
      showSource(block, "This diagram has no source.");
      continue;
    }
    try {
      const { svg } = await mermaid.render(`tfl-mermaid-${++counter}`, source);
      block.innerHTML = svg;
      block.dataset.state = "ready";
      const svgEl = block.querySelector("svg");
      if (svgEl) {
        svgEl.setAttribute("role", "img");
        if (!svgEl.querySelector("title")) {
          svgEl.setAttribute("aria-label", "Diagram");
        }
      }
    } catch {
      showSource(block, "This diagram could not be drawn. Its source is below.");
    }
  }
}

function showSource(block: HTMLElement, message: string): void {
  block.dataset.state = "failed";
  block.textContent = message;
  const pre = document.createElement("pre");
  pre.textContent = block.dataset.src ?? "";
  block.appendChild(pre);
}
